import { Task } from "@redux-saga/types";
import { Params } from "react-router-dom";
import {
  call,
  cancel,
  delay,
  fork,
  put,
  select,
  StrictEffect,
  take
} from "redux-saga/effects";

import { Entity } from "@/jenesys-types/data/common";
import { DataType } from "@/jenesys-types/data/enums";
import { extractError } from "@/jenesys-types/errors";

import { PathParams } from "@/services/api";
import { listData, retrieveData } from "@/store/data/data.saga";

import {
  getQueryKey,
  query,
  QueryAction,
  queryFailure,
  querySuccess,
  QueryType,
  selectQuery
} from "./queries";

function* performGetOneQuery(
  dataType: DataType,
  params: Params,
  pathParams: PathParams
): Generator<StrictEffect, void, any> {
  if (params.id) {
    try {
      const response = yield call(
        retrieveData,
        dataType,
        params.id as string,
        pathParams
      );
      yield put(
        querySuccess(QueryType.GetOne, dataType, params, pathParams, [
          response.id
        ])
      );
    } catch (error) {
      yield put(
        queryFailure(
          QueryType.GetOne,
          dataType,
          params,
          pathParams,
          extractError(error)
        )
      );
    }
  }
}

function* performListQuery(
  dataType: DataType,
  params: Params,
  pathParams: PathParams
): Generator<StrictEffect, void, any> {
  try {
    const response = yield call(
      listData,
      dataType,
      params,
      undefined,
      pathParams
    );
    yield put(
      querySuccess(
        QueryType.List,
        dataType,
        params,
        pathParams,
        response.data.map(({ id }: Entity) => id)
      )
    );
  } catch (error) {
    yield put(
      queryFailure(
        QueryType.List,
        dataType,
        params,
        pathParams,
        extractError(error)
      )
    );
  }
}

function* performQuery(
  action: QueryAction
): Generator<StrictEffect, void, any> {
  // Delay allows the query to be cancelled for debouncing.
  yield delay(50);

  const { type, dataType, params, pathParams } = action.payload;

  switch (type) {
    case QueryType.GetOne:
      yield call(performGetOneQuery, dataType, params, pathParams);
      break;
    case QueryType.List:
      yield call(performListQuery, dataType, params, pathParams);
      break;
    default:
      throw new Error(`Not yet implemented: ${type}`);
  }
}

export function* querySaga(): Generator<StrictEffect, void, any> {
  // Debounce the actual query, grouped by key.
  // See https://redux-saga.js.org/docs/recipes#debouncing
  const tasks: Record<string, Task> = {};

  while (true) {
    const action = yield take(query.type);

    const { type, dataType, forceFetch, params } = action.payload;
    const queryKey = getQueryKey(type, dataType, params);

    // Check if the query has already been loaded.
    const querySelector = yield call(selectQuery, type, dataType, params);
    const queryState = yield select(querySelector);

    // If the query hasn't been loaded or we're forcing a re-fetch,
    // then perform the query and cancel any ongoing identical queries
    if (forceFetch || !queryState?.isLoaded) {
      if (tasks[queryKey]) {
        yield cancel(tasks[queryKey]);
      }

      tasks[queryKey] = yield fork(performQuery, action);
    }
  }
}

export default function* dataSaga(): Generator<StrictEffect, void, any> {
  yield fork(querySaga);
}
